import vtkColorTransferFunction from '@kitware/vtk.js/Rendering/Core/ColorTransferFunction';
import type { VoiLutSequence } from 'generated/graphql';
import { logger } from 'modules/logger';
import { DEFAULT_VOI_LUT_SIZE } from 'generated/constants';

/**
 * Creates a vtkColorTransferFunction efficiently from large DICOM LUT data using the buildFunctionFromTable method
 * @param voiLutSequence - The VOI LUT sequence from DICOM tags
 * @returns A configured vtkColorTransferFunction instance or null if no LUT data
 */
export function createColorTransferFunctionFromLUTSequence(
  voiLutSequence: Nullable<VoiLutSequence>
): vtkColorTransferFunction | null {
  if (!voiLutSequence) return null;

  const { lutDescriptor, lutData } = voiLutSequence;

  // Extract LUT descriptor values
  const firstValueMapped = lutDescriptor[1];
  // Per DICOM standard, if descriptor entry 0 is 0, it means 2^16 entries
  const numberOfEntries = lutDescriptor[0] === 0 ? DEFAULT_VOI_LUT_SIZE : lutDescriptor[0];
  const bitsPerEntry = lutDescriptor[2];

  if (!lutData || lutData.length !== numberOfEntries) {
    logger.warn('Invalid LUT data: expected', numberOfEntries, 'entries, got', lutData?.length);
    return null;
  }

  // Calculate max value for normalization based the values in the LUT table
  // to ensure full dynamic range is met
  const maxValue = Math.max(...lutData);
  // Create a flat table of normalized RGB values
  const numberOfComponents = 3; // RGB
  // VTKjs operates on Float32Arrays
  const table = new Float32Array(numberOfEntries * numberOfComponents);

  for (let i = 0; i < numberOfEntries; i++) {
    // Normalize the LUT value to the [0, 1] range
    const normalizedValue = lutData[i] / (maxValue ?? 1);

    const idx = i * numberOfComponents;
    // For grayscale LUT, use the same value for R, G, and B
    table[idx] = normalizedValue; // R
    table[idx + 1] = normalizedValue; // G
    table[idx + 2] = normalizedValue; // B
  }

  // Create the transfer function instance
  const cfun = vtkColorTransferFunction.newInstance();

  // Build the LUT
  const xStart = firstValueMapped;
  const xEnd = firstValueMapped + numberOfEntries - 1;
  // vtkJS has this typed as number[], wants a list of normalized floats
  cfun.buildFunctionFromTable(xStart, xEnd, numberOfEntries, table as unknown as number[]);

  return cfun;
}

/**
 * Checks if VOI LUT should be used based on the existence of certain DICOM tags
 * @param voiLutSequence - The VOI LUT sequence from DICOM tags
 * @param windowCenter - Window center values
 * @param windowWidth - Window width values
 * @returns true if VOI LUT should be used instead of window/level
 */
export function shouldUseVoiLut(voiLutSequence: Nullable<VoiLutSequence>): boolean {
  // VOI LUT takes precedence over window/level if both are present
  return Boolean(voiLutSequence && voiLutSequence.lutData && voiLutSequence.lutData.length > 0);
}
